import { useEffect, useState } from 'react';
import { Card, Form, Button, Alert, Row, Col, Modal, Table, Badge, Tab, Tabs } from 'react-bootstrap';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  reposService,
  learnedContextService,
  auditService,
  findingsService,
  exceptionsService,
  ApiError,
} from '@/services/api';
import type { RepoPatch } from '@/services/api/repos-service';
import type { ExceptionRow, FindingRow } from '@/common/types';
import { useAsync } from '@/common/hooks/use-async';
import { AsyncBoundary } from '@/common/components/async-boundary';
import { useAuth } from '@/common/hooks/use-auth';
import {
  StackPicker,
  type SelectedStack,
  regionFromStackArn,
  stackNameFromArn,
} from '@/common/components/stack-picker';
import { EmailChipsInput } from '@/common/components/email-chips-input';

/** Deep-linkable tabs via ?tab=… — keeps the repo page from being one long scroll. */
const TABS = ['config', 'findings', 'learned', 'activity'] as const;
type TabKey = (typeof TABS)[number];

/** A labeled group of related settings — gives the form clear context boundaries. */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="border rounded p-3 mb-4">
      <legend className="float-none w-auto px-2 fs-6 text-uppercase text-muted">{title}</legend>
      {children}
    </fieldset>
  );
}

/** Split a typed resource key "ecr:name" / "lambda:fn" into [type, id]; legacy bare -> ecr. */
function parseResourceKey(key: string): [string, string] {
  const i = key.indexOf(':');
  return i < 0 ? ['ecr', key] : [key.slice(0, i), key.slice(i + 1)];
}

const RESOURCE_TYPE_META: Record<string, { label: string; bg: string }> = {
  ecr: { label: 'ECR image', bg: 'primary' },
  lambda: { label: 'Lambda', bg: 'warning' },
};

/** Edits the repo's typed monitoredResources list: chips grouped by type + add/remove.
 *  Keys are "ecr:<name>" | "lambda:<fn>"; most are auto-discovered from the stacks. */
function MonitoredResourcesEditor({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const [addType, setAddType] = useState<'ecr' | 'lambda'>('ecr');
  const [addId, setAddId] = useState('');

  const remove = (key: string) => onChange(value.filter((k) => k !== key));
  const add = () => {
    const id = addId.trim();
    if (!id) return;
    const key = `${addType}:${id}`;
    if (!value.includes(key)) onChange([...value, key]);
    setAddId('');
  };

  // Group for display.
  const groups: Record<string, string[]> = {};
  for (const key of value) {
    const [t, id] = parseResourceKey(key);
    (groups[t] ??= []).push(id);
  }

  return (
    <div>
      {value.length === 0 && (
        <div className="text-muted small mb-2">
          None yet — select the app's CloudFormation stack(s) above and Save to auto-discover, or
          add one manually below.
        </div>
      )}
      {Object.entries(groups).map(([type, ids]) => {
        const meta = RESOURCE_TYPE_META[type] ?? { label: type, bg: 'secondary' };
        return (
          <div key={type} className="mb-2">
            <div className="text-uppercase text-muted small mb-1">{meta.label}</div>
            <div className="d-flex flex-wrap gap-2">
              {ids.map((id) => (
                <Badge key={id} bg={meta.bg} className="d-flex align-items-center gap-1 fw-normal">
                  {id}
                  <span
                    role="button"
                    aria-label={`remove ${id}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => remove(`${type}:${id}`)}
                  >
                    ×
                  </span>
                </Badge>
              ))}
            </div>
          </div>
        );
      })}
      <Row className="g-2 mt-1 align-items-center">
        <Col xs="auto">
          <Form.Select
            size="sm"
            value={addType}
            onChange={(e) => setAddType(e.target.value as 'ecr' | 'lambda')}
          >
            <option value="ecr">ECR image</option>
            <option value="lambda">Lambda</option>
          </Form.Select>
        </Col>
        <Col xs="auto">
          <Form.Control
            size="sm"
            placeholder={addType === 'ecr' ? 'repository name' : 'function name'}
            value={addId}
            onChange={(e) => setAddId(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
          />
        </Col>
        <Col xs="auto">
          <Button size="sm" variant="outline-secondary" onClick={add} disabled={!addId.trim()}>
            Add
          </Button>
        </Col>
      </Row>
    </div>
  );
}

export default function RepoDetail() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const repo = params.get('repo') ?? '';
  const { actor } = useAuth();
  const { data, loading, error, reload } = useAsync(() => reposService.getRepo(repo), [repo]);

  const tabParam = params.get('tab');
  const activeTab: TabKey = (TABS as readonly string[]).includes(tabParam ?? '')
    ? (tabParam as TabKey)
    : 'config';
  // Deep-link the tab via ?tab=… (preserve ?repo=) so tabs are shareable and back/forward works.
  const selectTab = (key: string | null) => {
    const next = new URLSearchParams(params);
    next.set('tab', key ?? 'config');
    setParams(next, { replace: true });
  };

  const [form, setForm] = useState<RepoPatch>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [showOffboard, setShowOffboard] = useState(false);

  // Adapt the form's ARN string[] <-> the StackPicker's SelectedStack[] view. The ARN list
  // on the form stays the source of truth (that's what we PATCH); name/region are derived
  // for display from each ARN.
  const selectedStacks: SelectedStack[] = (form.cloudformationStackArns ?? []).map((arn) => ({
    stackArn: arn,
    stackName: stackNameFromArn(arn),
    region: regionFromStackArn(arn),
  }));
  const setSelectedStacks: React.Dispatch<React.SetStateAction<SelectedStack[]>> = (action) => {
    setForm((f) => {
      const current: SelectedStack[] = (f.cloudformationStackArns ?? []).map((arn) => ({
        stackArn: arn,
        stackName: stackNameFromArn(arn),
        region: regionFromStackArn(arn),
      }));
      const next = typeof action === 'function' ? (action as (p: SelectedStack[]) => SelectedStack[])(current) : action;
      return { ...f, cloudformationStackArns: next.map((s) => s.stackArn) };
    });
  };

  useEffect(() => {
    if (data?.repo) {
      const r = data.repo;
      setForm({
        enabled: Boolean(r.enabled),
        policy: (r.policy as string) ?? 'assist',
        defaultBranch: (r.defaultBranch as string) ?? '',
        // Typed monitored resources; fall back to legacy ecrRepoNames (bare names -> ecr:<name>).
        monitoredResources:
          (r.monitoredResources as string[]) ??
          ((r.ecrRepoNames as string[]) ?? []).map((n) => `ecr:${n}`),
        owners: r.owners ?? [],
        branchPattern: r.branchPattern ?? '',
        exceptionBranchPattern: r.exceptionBranchPattern ?? '',
        ciEnabled: Boolean(r.ciEnabled),
        requireValidation: Boolean(r.requireValidation),
        aiContextRoot: (r.aiContextRoot as string) ?? 'ai_context',
        cloudformationStackArns: (r.cloudformationStackArns as string[]) ?? [],
      });
    }
  }, [data]);

  async function save() {
    setBusy(true);
    setMsg(null);
    try {
      await reposService.patchRepo(repo, actor, form);
      setMsg({ ok: true, text: 'Saved' });
      reload();
    } catch (e) {
      const t = e instanceof ApiError ? `${e.status}: ${e.message}` : String(e);
      setMsg({ ok: false, text: `Save failed — ${t}` });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <Card.Header>
        <span>Repo: {repo}</span>
      </Card.Header>
      <Card.Body>
        <AsyncBoundary loading={loading} error={error}>
          {data && (
            <Tabs activeKey={activeTab} onSelect={selectTab} className="mb-3">
              {/* --- Configuration --- */}
              <Tab eventKey="config" title="Configuration">
                <Form onSubmit={(e) => e.preventDefault()}>
                  {msg && <Alert variant={msg.ok ? 'success' : 'danger'}>{msg.text}</Alert>}

                  {/* --- Status --- */}
                  <Section title="Status">
                    <Form.Check
                      type="switch"
                      label="Enabled"
                      checked={Boolean(form.enabled)}
                      onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                    />
                    <Form.Text className="text-muted d-block">
                      When on, vuln-sentinel acts on Inspector findings for this repo and opens
                      draft fix PRs a human reviews before merge (it never merges on its own). Turn
                      off to pause all remediation while keeping the repo onboarded.
                    </Form.Text>
                  </Section>

                  {/* --- Monitored resources & discovery --- */}
                  <Section title="Monitored resources">
                    <p className="text-muted">
                      Browse a region, filter, and select the CloudFormation stack(s) that make up
                      this app. vuln-sentinel reads these stacks (now and daily) and auto-discovers
                      the Inspector-scannable resources in them (ECR images and Lambda functions) —
                      so adding a resource to a stack is picked up without re-onboarding. Saving
                      re-runs discovery immediately.
                    </p>
                    <Form.Group className="mb-3">
                      <Form.Label>CloudFormation stacks</Form.Label>
                      <StackPicker selected={selectedStacks} setSelected={setSelectedStacks} />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Monitored resources</Form.Label>
                      <MonitoredResourcesEditor
                        value={form.monitoredResources ?? []}
                        onChange={(monitoredResources) => setForm({ ...form, monitoredResources })}
                      />
                      <Form.Text className="text-muted">
                        The Inspector-scannable resources vuln-sentinel watches for this repo —
                        ECR images and Lambda functions. Normally <strong>auto-populated</strong>{' '}
                        from the selected stacks (that's what links a scanned resource to its
                        source). Add one manually only for a resource not in a stack you can point
                        at; discovered resources are merged in and manual ones are kept.
                      </Form.Text>
                    </Form.Group>
                  </Section>

                  {/* --- Branches --- */}
                  <Section title="Branches">
                    <Form.Group className="mb-3">
                      <Form.Label>Default branch</Form.Label>
                      <Form.Control
                        value={form.defaultBranch ?? ''}
                        placeholder="e.g. main, sandbox, develop"
                        onChange={(e) => setForm({ ...form, defaultBranch: e.target.value })}
                      />
                      <Form.Text className="text-muted">
                        The branch fixes branch off and PRs target — this repo's integration branch
                        for this environment.
                      </Form.Text>
                    </Form.Group>
                    <Row>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Fix branch pattern</Form.Label>
                          <Form.Control
                            value={form.branchPattern ?? ''}
                            onChange={(e) => setForm({ ...form, branchPattern: e.target.value })}
                          />
                          <Form.Text className="text-muted">
                            Branch name for the repo's fix PR. <code>{'{type}'}</code> ={' '}
                            <code>fix</code>, <code>{'{groupKey}'}</code> = <code>all</code> (one
                            PR per repo covers all vulnerabilities), <code>{'{date}'}</code> = UTC{' '}
                            <code>YYYYMMDD</code>. e.g. <code>vuln-sentinel/fix-all-20261104</code>.
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Exception branch pattern</Form.Label>
                          <Form.Control
                            value={form.exceptionBranchPattern ?? ''}
                            onChange={(e) => setForm({ ...form, exceptionBranchPattern: e.target.value })}
                          />
                          <Form.Text className="text-muted">
                            Name for documentation-only exception PRs — set a prefix your CI skips.
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Section>

                  {/* --- AI context --- */}
                  <Section title="AI context">
                    <Form.Group>
                      <Form.Label>AI context root</Form.Label>
                      <Form.Control
                        value={form.aiContextRoot ?? ''}
                        placeholder="ai_context"
                        onChange={(e) => setForm({ ...form, aiContextRoot: e.target.value })}
                      />
                      <Form.Text className="text-muted">
                        Where this repo keeps its security AI context, relative to the repo root
                        (default <code>ai_context</code>). The agent clones the repo and reads
                        markdown under this folder (the <code>container-vulnerabilities</code>
                        subfolder underneath is still auto-detected). Set it if your team keeps
                        context elsewhere, e.g. <code>docs/security</code> — no repo change needed
                        to onboard.
                      </Form.Text>
                    </Form.Group>
                  </Section>

                  {/* --- Notifications --- */}
                  <Section title="Notifications">
                    <Form.Group>
                      <Form.Label>Owners</Form.Label>
                      <EmailChipsInput
                        value={form.owners ?? []}
                        onChange={(owners) => setForm({ ...form, owners })}
                        placeholder="dev@genesys.com — press Enter to add"
                      />
                      <Form.Text className="text-muted">
                        The people responsible for this repo — and who get notified when
                        vuln-sentinel opens a fix PR or drafts an exception. Add a distribution list
                        to alert a channel instead of individuals.
                      </Form.Text>
                    </Form.Group>
                  </Section>

                  <Button type="button" onClick={save} disabled={busy}>Save</Button>

                  {/* --- Danger zone (pinned at the bottom of config, settings-page convention) --- */}
                  <fieldset className="border border-danger rounded p-3 mt-5">
                    <legend className="float-none w-auto px-2 fs-6 text-uppercase text-danger">
                      Danger zone
                    </legend>
                    <p className="mb-2">
                      <strong>Offboard this repo.</strong> This permanently deletes vuln-sentinel's
                      configuration for <code>{repo}</code> — its image mappings, branch settings,
                      owners, AI-context root, and selected stacks — and stops ingesting its
                      Inspector findings. <strong>This cannot be undone.</strong> To monitor it again
                      you'd have to onboard and reconfigure it from scratch.
                    </p>
                    <p className="text-muted small mb-2">
                      Just want to pause temporarily? Turn off <strong>Enabled</strong> above
                      instead — that keeps all configuration and resumes instantly when switched
                      back on.
                    </p>
                    <Button type="button" variant="outline-danger" onClick={() => setShowOffboard(true)} disabled={busy}>
                      Offboard repo…
                    </Button>
                  </fieldset>
                </Form>
              </Tab>

              {/* --- Findings --- */}
              <Tab eventKey="findings" title="Findings">
                <FindingsSection repo={repo} actor={actor} />
              </Tab>

              {/* --- Learned context (M4 Phase 2) --- */}
              <Tab eventKey="learned" title="Learned context">
                <LearnedContextSection repo={repo} actor={actor} />
              </Tab>

              {/* --- Activity --- */}
              <Tab eventKey="activity" title="Activity">
                <ActivitySection repo={repo} />
              </Tab>
            </Tabs>
          )}
        </AsyncBoundary>
      </Card.Body>

      <OffboardModal
        show={showOffboard}
        repo={repo}
        busy={busy}
        onClose={() => setShowOffboard(false)}
        onConfirm={async () => {
          setBusy(true);
          setMsg(null);
          try {
            await reposService.offboardRepo(repo, actor);
            navigate('/repos', { replace: true });
          } catch (e) {
            const t = e instanceof ApiError ? `${e.status}: ${e.message}` : String(e);
            setMsg({ ok: false, text: `Offboard failed — ${t}` });
            setShowOffboard(false);
          } finally {
            setBusy(false);
          }
        }}
      />
    </Card>
  );
}

/** Destructive-offboard confirmation: requires typing the exact repo to enable the button. */
function OffboardModal({
  show,
  repo,
  busy,
  onClose,
  onConfirm,
}: {
  show: boolean;
  repo: string;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const [confirmText, setConfirmText] = useState('');
  const matches = confirmText.trim() === repo;
  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title className="text-danger">Offboard {repo}?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          This <strong>permanently deletes</strong> all vuln-sentinel configuration for this repo
          and stops ingesting its findings. It <strong>cannot be undone</strong> — re-adding it
          later means onboarding and reconfiguring from scratch.
        </p>
        <Form.Group>
          <Form.Label>
            Type <code>{repo}</code> to confirm:
          </Form.Label>
          <Form.Control
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={repo}
            autoFocus
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button type="button" variant="danger" onClick={onConfirm} disabled={!matches || busy}>
          {busy ? 'Offboarding…' : 'Offboard repo'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/** A repo's Inspector findings: severity, package/fix, and (for suppressed ones) the
 *  covering exception + SNOW REQ with an aging/expiry badge. Hosts the on-demand
 *  "Pull latest findings" backfill button. */
function FindingsSection({ repo, actor }: { repo: string; actor: string }) {
  const findings = useAsync(() => findingsService.listFindings({ repo }), [repo]);
  const exceptions = useAsync(() => exceptionsService.listExceptions({ repo }), [repo]);
  const [pulling, setPulling] = useState(false);
  const [pullMsg, setPullMsg] = useState<{ ok: boolean; text: string } | null>(null);
  // The finding whose "Attach existing REQ" modal is open (null = closed).
  const [attachTarget, setAttachTarget] = useState<FindingRow | null>(null);
  const [attachMsg, setAttachMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Index exceptions by CVE so a finding can show its REQ + expiry even for in-flight
  // (DRAFTED/APPROVED) exceptions, not just the suppressedByReq stamped on the row.
  const excByCve = new Map<string, ExceptionRow>();
  for (const e of exceptions.data?.items ?? []) {
    if (e.cveId && !excByCve.has(e.cveId)) excByCve.set(e.cveId, e);
  }

  async function pull() {
    setPulling(true);
    setPullMsg(null);
    try {
      const res = await reposService.pullFindings(repo, actor);
      const text = res.note
        ? `Nothing to pull — ${res.note}.`
        : `Queued ${res.pulled} finding${res.pulled === 1 ? '' : 's'} for processing. ` +
          'They flow through the normal pipeline — grouped PRs appear shortly (duplicates are ignored).';
      setPullMsg({ ok: true, text });
      // Give the pipeline a moment, then refresh the list.
      setTimeout(() => findings.reload(), 1500);
    } catch (e) {
      const t = e instanceof ApiError ? `${e.status}: ${e.message}` : String(e);
      setPullMsg({ ok: false, text: `Pull failed — ${t}` });
    } finally {
      setPulling(false);
    }
  }

  const [showResolved, setShowResolved] = useState(false);
  const [image, setImage] = useState('__all__');
  const allItems = findings.data?.items ?? [];
  // Distinct images (ECR repos / Lambdas) this repo's findings belong to. A repo has a
  // stable, small set (e.g. ember = ember-sandbox + ember-sandbox-schedule). Filtering by
  // image lets you read "the true vuln count for THIS image" — cross-image dedup is a call
  // only the dev team can make, so the UI exposes the distinction instead of assuming it.
  const images = Array.from(
    new Set(allItems.map((f) => imageLabel(f.resourceKey)).filter(Boolean)),
  ).sort();
  const items =
    image === '__all__'
      ? allItems
      : allItems.filter((f) => imageLabel(f.resourceKey) === image);
  // Collapse the raw finding rows (one per image digest — duplicated) into ONE row per CVE,
  // so the tab is consumable. Each group keeps its occurrences for the expandable detail.
  // Open-only by default; terminal rows (incl. superseded builds) hidden behind a toggle.
  const groups = groupFindings(items, { includeTerminal: showResolved });

  return (
    <fieldset className="border rounded p-3">
      <legend className="float-none w-auto px-2 fs-6 text-uppercase text-muted">Findings</legend>
      <div className="d-flex justify-content-between align-items-start mb-2 gap-3">
        <p className="text-muted small mb-0">
          AWS Inspector findings for this repo's monitored resources. vuln-sentinel ingests
          findings as Inspector emits them (on deploy/scan). To catch up a repo's existing backlog
          without a redeploy — handy right after onboarding, or when a pipeline is blocked by the
          very vulns it would scan — use <strong>Pull latest findings</strong>. Safe to run anytime;
          duplicates are ignored.
        </p>
        <Button
          type="button"
          variant="outline-primary"
          size="sm"
          className="flex-shrink-0"
          onClick={pull}
          disabled={pulling}
        >
          {pulling ? 'Pulling…' : 'Pull latest findings'}
        </Button>
      </div>
      <div className="d-flex justify-content-between align-items-center mb-2 gap-3 flex-wrap">
        <div className="d-flex align-items-center gap-2">
          {images.length > 1 && (
            <Form.Select
              size="sm"
              value={image}
              onChange={(e) => setImage(e.target.value)}
              style={{ width: 'auto' }}
              aria-label="Filter by image"
            >
              <option value="__all__">All images ({images.length})</option>
              {images.map((img) => (
                <option key={img} value={img}>
                  {img}
                </option>
              ))}
            </Form.Select>
          )}
          <span className="text-muted small">
            {groups.length} distinct {groups.length === 1 ? 'CVE' : 'CVEs'}
            {!showResolved && ' (open)'}
            {image === '__all__' && images.length > 1 && ' across all images'}
          </span>
        </div>
        <Form.Check
          type="switch"
          id="show-resolved-findings"
          label="Show resolved/closed"
          checked={showResolved}
          onChange={(e) => setShowResolved(e.target.checked)}
          className="small"
        />
      </div>
      {pullMsg && (
        <Alert
          variant={pullMsg.ok ? 'success' : 'danger'}
          className="py-1 px-2 small"
          dismissible
          onClose={() => setPullMsg(null)}
        >
          {pullMsg.text}
        </Alert>
      )}
      {attachMsg && (
        <Alert
          variant={attachMsg.ok ? 'success' : 'danger'}
          className="py-1 px-2 small"
          dismissible
          onClose={() => setAttachMsg(null)}
        >
          {attachMsg.text}
        </Alert>
      )}
      <AsyncBoundary loading={findings.loading} error={findings.error}>
        <Table responsive size="sm" hover className="mb-0 align-middle">
          <thead>
            <tr>
              <th style={{ width: 32 }}></th>
              <th>Severity</th>
              <th>CVE</th>
              <th>Package</th>
              <th>Installed → Fixed</th>
              <th>Status</th>
              <th>Images</th>
              <th>Exception / REQ</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <CveGroupRow
                key={g.cveId}
                group={g}
                exc={excByCve.get(g.cveId)}
                onAttach={() => setAttachTarget(g.representative)}
              />
            ))}
            {!groups.length && (
              <tr>
                <td colSpan={9} className="text-muted small">
                  {items.length
                    ? 'No open findings — everything here is resolved or closed. Toggle “Show resolved/closed” to view them.'
                    : 'No findings recorded yet. If this repo has a known backlog, click “Pull latest findings”.'}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </AsyncBoundary>

      <AttachReqModal
        repo={repo}
        actor={actor}
        finding={attachTarget}
        onClose={() => setAttachTarget(null)}
        onDone={(text) => {
          setAttachTarget(null);
          setAttachMsg({ ok: true, text });
          // Refresh so the finding flips to SUPPRESSED with its new REQ.
          setTimeout(() => {
            findings.reload();
            exceptions.reload();
          }, 1200);
        }}
      />
    </fieldset>
  );
}

/** Modal to attach an existing ServiceNow REQ to a finding's CVE (Layer 2). */
function AttachReqModal({
  repo,
  actor,
  finding,
  onClose,
  onDone,
}: {
  repo: string;
  actor: string;
  finding: FindingRow | null;
  onClose: () => void;
  onDone: (text: string) => void;
}) {
  const [req, setReq] = useState('');
  const [filedDate, setFiledDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset fields whenever a different finding opens the modal.
  useEffect(() => {
    setReq('');
    setFiledDate('');
    setErr(null);
  }, [finding]);

  const show = finding !== null;
  const valid = req.trim() !== '' && /^\d{4}-\d{2}-\d{2}$/.test(filedDate.trim());

  async function submit() {
    if (!finding) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await exceptionsService.attachException(actor, {
        repo,
        cveIds: [finding.cveId],
        reqNumber: req.trim(),
        filedDate: filedDate.trim(),
        findingArns: [finding.findingArn],
        component: finding.component,
        groupKey: finding.groupKey,
      });
      onDone(
        `Attached ${res.reqNumber} to ${finding.cveId}. It's now suppressed until ` +
          `re-evaluation (${new Date(res.expiresAt * 1000).toLocaleDateString()}).`,
      );
    } catch (e) {
      setErr(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Attach existing ServiceNow exception</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted">
          Record a ServiceNow exception your team already filed for{' '}
          <code>{finding?.cveId}</code>. vuln-sentinel will <strong>suppress</strong> this CVE (no
          fix PR) until its 90-day re-evaluation window — counted from the filing date you enter —
          lapses. vuln-sentinel never files the exception itself; this just records one that exists.
        </p>
        {err && <Alert variant="danger" className="py-1 px-2 small">{err}</Alert>}
        <Form.Group className="mb-3">
          <Form.Label>ServiceNow REQ number</Form.Label>
          <Form.Control
            value={req}
            placeholder="e.g. REQ0012345"
            onChange={(e) => setReq(e.target.value)}
            autoFocus
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Filed date</Form.Label>
          <Form.Control
            type="date"
            value={filedDate}
            onChange={(e) => setFiledDate(e.target.value)}
          />
          <Form.Text className="text-muted">
            The date the ServiceNow exception was filed. The 90-day re-evaluation window is counted
            from this date.
          </Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button type="button" variant="primary" onClick={submit} disabled={!valid || busy}>
          {busy ? 'Attaching…' : 'Attach REQ'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/** Sort rank for a severity (lower = more urgent). Unknown sorts last. */
function severityRank(sev?: string): number {
  const order: Record<string, number> = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
  return order[String(sev ?? '').toUpperCase()] ?? 9;
}

const SEV_BADGE: Record<string, string> = {
  CRITICAL: 'danger',
  HIGH: 'warning',
  MEDIUM: 'info',
  LOW: 'secondary',
};

// Finding statuses that represent OPEN, actionable work (vs terminal history). Mirrors the
// rollup's ACTIVE set; terminal statuses (MERGED/CLOSED/RESOLVED) are hidden unless toggled.
const OPEN_STATUSES = new Set(['PENDING_GROUP', 'GROUPED', 'IN_PROGRESS', 'PR_OPEN', 'SUPPRESSED', 'NO_FIX_LOGGED']);

const STATUS_BADGE: Record<string, string> = {
  PENDING_GROUP: 'primary',
  GROUPED: 'primary',
  IN_PROGRESS: 'primary',
  PR_OPEN: 'primary',
  NO_FIX_LOGGED: 'warning',
  SUPPRESSED: 'secondary',
  MERGED: 'success',
  RESOLVED: 'success',
  CLOSED: 'dark',
  SUPERSEDED: 'light',
};

/** One distinct CVE, collapsed from all its per-image/per-resource finding rows. */
interface CveGroup {
  cveId: string;
  severity: string;
  packageName: string;
  installed: string;
  fixedIn: string;
  req?: string;
  statusCounts: Record<string, number>;
  dominantStatus: string;
  imageCount: number;
  occurrences: FindingRow[];
  representative: FindingRow;
}

/** Collapse raw finding rows into one group per CVE. Inspector reports a CVE once per image
 *  digest / ECR repo, so the raw list is 30x duplicated; this makes the tab consumable. */
function groupFindings(
  items: FindingRow[],
  opts: { includeTerminal: boolean },
): CveGroup[] {
  const byCve = new Map<string, FindingRow[]>();
  for (const f of items) {
    const cve = f.cveId || '(unknown)';
    (byCve.get(cve) ?? byCve.set(cve, []).get(cve)!).push(f);
  }

  const groups: CveGroup[] = [];
  for (const [cveId, rows] of byCve) {
    const open = rows.filter((r) => OPEN_STATUSES.has(String(r.status ?? '')));
    // Default view shows only CVEs with open work; toggle reveals fully-resolved ones.
    if (!opts.includeTerminal && open.length === 0) continue;

    const statusCounts: Record<string, number> = {};
    for (const r of rows) {
      const s = String(r.status ?? 'UNKNOWN');
      statusCounts[s] = (statusCounts[s] ?? 0) + 1;
    }
    // Worst severity across occurrences.
    const severity = rows
      .map((r) => String(r.severity ?? 'UNKNOWN').toUpperCase())
      .sort((a, b) => severityRank(a) - severityRank(b))[0];
    // Representative package detail (first row that has one).
    const rep = rows.find((r) => (r.vulnerablePackages ?? []).length) ?? rows[0];
    const pkg = (rep.vulnerablePackages ?? [])[0];
    const req =
      rows.map((r) => r.suppressedByReq).find(Boolean) || undefined;
    // Dominant status = the open one with the most occurrences, else any.
    const dominantStatus =
      Object.entries(statusCounts)
        .filter(([s]) => OPEN_STATUSES.has(s))
        .sort((a, b) => b[1] - a[1])[0]?.[0] ??
      Object.entries(statusCounts).sort((a, b) => b[1] - a[1])[0][0];
    const images = new Set(
      rows.map((r) => String(r.resourceId ?? r.resourceKey ?? '')).filter(Boolean),
    );

    groups.push({
      cveId,
      severity,
      packageName: pkg?.name ?? '-',
      installed: pkg?.version ?? '?',
      fixedIn: pkg?.fixedInVersion ?? '',
      req,
      statusCounts,
      dominantStatus,
      imageCount: images.size,
      occurrences: rows,
      representative: rep,
    });
  }

  // Sort by severity (worst first), then CVE id.
  groups.sort((a, b) => {
    const s = severityRank(a.severity) - severityRank(b.severity);
    return s !== 0 ? s : a.cveId.localeCompare(b.cveId);
  });
  return groups;
}

/** One collapsed CVE row with an expandable per-image/per-resource breakdown. */
function CveGroupRow({
  group,
  exc,
  onAttach,
}: {
  group: CveGroup;
  exc?: ExceptionRow;
  onAttach: () => void;
}) {
  const [open, setOpen] = useState(false);
  const sev = group.severity;
  const req = group.req || exc?.serviceNowReqNumber;
  const canAttach = !req;
  // Status cell: the dominant open status + a count of distinct statuses if mixed.
  const statusKeys = Object.keys(group.statusCounts);
  const mixed = statusKeys.length > 1;

  return (
    <>
      <tr>
        <td>
          <Button
            type="button"
            variant="link"
            size="sm"
            className="p-0 text-decoration-none"
            aria-label={open ? 'collapse' : 'expand'}
            onClick={() => setOpen((o) => !o)}
          >
            {open ? '▾' : '▸'}
          </Button>
        </td>
        <td>
          <Badge bg={SEV_BADGE[sev] ?? 'light'}>{sev}</Badge>
        </td>
        <td className="small">{group.cveId}</td>
        <td className="small">{group.packageName}</td>
        <td className="small">
          {group.installed} → {group.fixedIn || <span className="text-muted">none</span>}
        </td>
        <td>
          <Badge bg={STATUS_BADGE[group.dominantStatus] ?? 'light'}>{group.dominantStatus}</Badge>
          {mixed && <span className="text-muted small ms-1">+{statusKeys.length - 1}</span>}
        </td>
        <td className="small text-muted">
          {group.imageCount} {group.imageCount === 1 ? 'image' : 'images'}
        </td>
        <td className="small">
          {req ? (
            <span>
              {req} <ExceptionAging exc={exc} />
            </span>
          ) : (
            <span className="text-muted">—</span>
          )}
        </td>
        <td className="text-end">
          {canAttach && (
            <Button type="button" size="sm" variant="outline-secondary" onClick={onAttach}>
              Attach REQ
            </Button>
          )}
        </td>
      </tr>
      {open && (
        <tr>
          <td></td>
          <td colSpan={8} className="bg-light">
            <div className="small mb-1 text-muted">
              {group.occurrences.length} occurrence
              {group.occurrences.length === 1 ? '' : 's'} across images/resources:
            </div>
            <Table size="sm" borderless className="mb-0">
              <thead>
                <tr className="text-muted small">
                  <th>Status</th>
                  <th>Resource</th>
                  <th>Image digest</th>
                </tr>
              </thead>
              <tbody>
                {group.occurrences.map((o) => (
                  <tr key={o.findingArn}>
                    <td>
                      <Badge bg={STATUS_BADGE[String(o.status ?? '')] ?? 'light'}>
                        {o.status ?? '-'}
                      </Badge>
                    </td>
                    <td className="small font-monospace">
                      {shortResource(o.resourceKey ?? o.resourceId)}
                    </td>
                    <td className="small font-monospace text-muted">
                      {shortDigest(o)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </td>
        </tr>
      )}
    </>
  );
}

/** Trim a typed resource key / ARN to something readable (e.g. "ecr:ember-sandbox"). */
function shortResource(key?: string): string {
  if (!key) return '-';
  if (key.includes(':repository/')) return 'ecr:' + key.split(':repository/')[1].split('/')[0];
  if (key.includes(':function:')) return 'lambda:' + key.split(':function:')[1].split(':')[0];
  return key;
}

/** The image an occurrence belongs to, from its typed resourceKey ("ecr:<name>" |
 *  "lambda:<fn>"). The bare name (e.g. "ember-sandbox") is what the user filters by. */
function imageLabel(resourceKey?: string): string {
  if (!resourceKey) return '';
  const i = resourceKey.indexOf(':');
  return i < 0 ? resourceKey : resourceKey.slice(i + 1);
}

/** Short image digest for the occurrence detail (last 12 hex of the sha256), if present. */
function shortDigest(f: FindingRow): string {
  const d = String((f as { imageDigest?: string }).imageDigest ?? '');
  if (d.startsWith('sha256:')) return d.slice(7, 19);
  const id = String(f.resourceId ?? '');
  const i = id.indexOf('sha256:');
  return i >= 0 ? id.slice(i + 7, i + 19) : '-';
}

/** Aging/expiry badge derived from an exception's expiresAt vs now (90-day re-eval). */
function ExceptionAging({ exc }: { exc?: ExceptionRow }) {
  if (!exc?.expiresAt) return null;
  const now = Math.floor(Date.now() / 1000);
  const days = Math.round((exc.expiresAt - now) / 86400);
  if (days <= 0) return <Badge bg="danger">expired — re-evaluate</Badge>;
  if (days <= 14) return <Badge bg="warning">expires in {days}d</Badge>;
  return <Badge bg="light" text="dark">expires in {days}d</Badge>;
}

/** vuln-sentinel's own learned lessons for this repo (loadContext layer 3), with retire. */
function LearnedContextSection({ repo, actor }: { repo: string; actor: string }) {
  const { data, loading, error, reload } = useAsync(
    () => learnedContextService.listLearnedContext(repo),
    [repo],
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  async function retire(entryId: string) {
    setBusy(entryId);
    setMsg(null);
    try {
      await learnedContextService.retireLearnedContext(repo, entryId, actor);
      reload();
    } catch (e) {
      setMsg(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    } finally {
      setBusy(null);
    }
  }

  const items = data?.items ?? [];
  return (
    <fieldset className="border rounded p-3">
      <legend className="float-none w-auto px-2 fs-6 text-uppercase text-muted">Learned context</legend>
      <p className="text-muted small">
        Lessons vuln-sentinel distilled from feedback on this repo. The agent reloads the
        scope-relevant ones on future runs (loadContext layer 3). Retire a lesson to stop loading it.
      </p>
      {msg && <Alert variant="danger" className="py-1 px-2 small">{msg}</Alert>}
      {loading && <div className="text-muted small">Loading…</div>}
      {error && <Alert variant="danger" className="py-1 px-2 small mb-0">{error}</Alert>}
      {!loading && !error && (
        <Table responsive size="sm" className="mb-0">
          <thead>
            <tr><th>Scope</th><th>Lesson</th><th>Conf.</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((l) => (
              <tr key={l.entryId}>
                <td><Badge bg="info">{l.scope}</Badge></td>
                <td>{l.content}</td>
                <td className="small text-muted">{l.confidence ?? '-'}</td>
                <td>
                  <Button
                    type="button" size="sm" variant="outline-secondary"
                    disabled={busy === l.entryId}
                    onClick={() => retire(l.entryId)}
                  >
                    {busy === l.entryId ? '…' : 'Retire'}
                  </Button>
                </td>
              </tr>
            ))}
            {!items.length && (
              <tr><td colSpan={4} className="text-muted small">No learned lessons yet.</td></tr>
            )}
          </tbody>
        </Table>
      )}
    </fieldset>
  );
}

/** Recent audit activity for this repo (newest-first), embedded from the audit feed. */
function ActivitySection({ repo }: { repo: string }) {
  const { data, loading, error } = useAsync(
    () => auditService.listAudit({ repo, limit: 15 }),
    [repo],
  );
  const items = data?.items ?? [];
  return (
    <fieldset className="border rounded p-3">
      <legend className="float-none w-auto px-2 fs-6 text-uppercase text-muted">
        Recent activity
      </legend>
      {loading && <div className="text-muted small">Loading…</div>}
      {error && <Alert variant="danger" className="py-1 px-2 small mb-0">{error}</Alert>}
      {!loading && !error && (
        <Table responsive size="sm" className="mb-0">
          <thead><tr><th>When</th><th>Action</th><th>By</th></tr></thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.auditId}>
                <td className="small">{a.timestamp ? new Date(a.timestamp).toLocaleString() : '-'}</td>
                <td><Badge bg="secondary">{a.action}</Badge></td>
                <td className="small">{a.actor}</td>
              </tr>
            ))}
            {!items.length && (
              <tr><td colSpan={3} className="text-muted small">No activity yet.</td></tr>
            )}
          </tbody>
        </Table>
      )}
      <Link to={`/audit?repo=${encodeURIComponent(repo)}`} className="small">Full activity →</Link>
    </fieldset>
  );
}
